export type Gender = 'M' | 'F';

export type AthleteAgeCategory =
  | 'MLADSI_ZAK'
  | 'STARSI_ZAK'
  | 'MLADSI_DOROSTENEC'
  | 'STREDNI_DOROSTENEC'
  | 'STARSI_DOROSTENEC'
  | 'MLADSI_DOROSTENKA'
  | 'STREDNI_DOROSTENKA'
  | 'STARSI_DOROSTENKA'
  | 'MUZ'
  | 'ZENA'
  | 'OSTATNI';

export type AgeGroup = 'MLADSI_ZACI' | 'STARSI_ZACI' | 'DOROSTENCI' | 'DOROSTENKY' | 'MUZI' | 'ZENY' | 'OSTATNI';

export type CompetitionStatus = 'DRAFT' | 'LIVE' | 'FINISHED';

export type PenaltyTrackingMode = 'NONE' | 'SIMPLE' | 'DETAILED';

export type DisciplineType =
  | 'POZARNI_UTOK'
  | 'STAFETA_DVOJIC'
  | 'STAFETA_4X60'
  | 'STAFETA_4X100'
  | 'BEH_60M_PREKAZKY'
  | 'BEH_100M_PREKAZKY'
  | 'BEH_100M_PHP'
  | 'POZARNI_UTOK_CTIF'
  | 'STAFETA_CTIF'
  | 'VEZ';

export type Lane = 'LP' | 'PP' | 'TRACK_1' | 'TRACK_2';

export type TimeFormat = 'MM_SS_CC' | 'SS_CC';

export type PublicResultsLayout = 'CLASSIC' | 'PODIUM' | 'SPLIT_VIEW';

export type PublicThemeMode = 'AUTO' | 'LIGHT' | 'DARK';

export type BoardLayout = 'MATRIX' | 'FOCUS' | 'CAROUSEL' | 'SPLIT' | 'COLUMNS';

export type BoardSortMode = 'RANK' | 'START_NUMBER';

export interface PublicDisplayConfig {
  layout: PublicResultsLayout;
  showStartNumber: boolean;
  showPenaltySeconds: boolean;
  showPoints: boolean;
  showAttemptDetails: boolean;
  showLaneDetails: boolean;
  showNote: boolean;
  rotationSeconds: number;
  rotateOverall: boolean;
  autoRefreshSeconds: number;
  themeMode: PublicThemeMode;
  showLogo: boolean;
  showOrganizerLogo: boolean;
  organizerLogoUrl: string | null;
}

export interface Unit {
  id: number;
  name: string;
  slug: string;
  isActive: boolean;
  /** Super admin capability: muze admin jednotky skryt soutez z rozcestniku? */
  canHideFromLanding: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: number;
  unitId: number | null;
  unitName: string | null;
  username: string;
  email: string | null;
  displayName: string;
  role: 'SUPER_ADMIN' | 'UNIT_ADMIN' | 'RECORDER';
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface Competition {
  id: number;
  unitId: number;
  unitName: string | null;
  unitSlug: string | null;
  shortCode: string;
  /** Neverejny token pro URL /board/{token} — plat pro pořadatele. */
  boardToken: string | null;
  name: string;
  competitionDate: string;
  location: string | null;
  status: CompetitionStatus;
  npPenaltyRank: number | null;
  tieBreakerEnabled: boolean;
  penaltyTrackingMode: PenaltyTrackingMode;
  stafetaDvojicAttempts: number;
  stafetaDvojicParallelTracks: number;
  overallRankingVisible: boolean;

  // Konfigurace verejneho zobrazeni
  publicResultsLayout: PublicResultsLayout;
  publicShowStartNumber: boolean;
  publicShowPenaltySeconds: boolean;
  publicShowPoints: boolean;
  publicShowAttemptDetails: boolean;
  publicShowLaneDetails: boolean;
  publicShowNote: boolean;
  publicRotationSeconds: number;
  publicRotateOverall: boolean;
  publicAutoRefreshSeconds: number;
  publicThemeMode: PublicThemeMode;
  publicShowLogo: boolean;
  publicShowOrganizerLogo: boolean;
  publicOrganizerLogoUrl: string | null;
  publicOrganizerLogoMime: string | null;
  /** Skalovani velikosti organizer loga v procentech (50-200, default 100). */
  publicOrganizerLogoScale: number;

  boardLayout: BoardLayout;
  boardThemeMode: PublicThemeMode;
  /** Skalovani pisma boardu v procentech (50-200, default 100). */
  boardFontScale: number;
  /** Zobrazit vsechna druzstva (i bez vysledku) na boardu? */
  boardShowAllTeams: boolean;
  /** Razeni na boardu: RANK (default) / START_NUMBER. */
  boardSortMode: BoardSortMode;
  /** Zobrazit casy jednotlivych drah (LP/PP, Draha 1/2) pod urednim casem na boardu? */
  boardShowLaneDetails: boolean;
  /** Zobrazit sporic na boardu dokud je soutez DRAFT? */
  boardScreensaverEnabled: boolean;
  /** Volitelny text na sporici (napr. "Start soutěže 9:00"). */
  boardCustomText: string | null;
  /** Volitelny disclaimer ve footeru verejne stranky souteze. */
  publicDisclaimerText: string | null;

  /** Je soutez verejne viditelna (landing + /s/{shortCode})? */
  publicVisible: boolean;
  /** ISO datetime planovaneho zverejneni, null = zverejnene ihned (pokud publicVisible=true). */
  publishAt: string | null;
  /** Zobrazit soutez na rozcestniku (root)? Default true. Skryti je dostupne jen kdyz unit ma capability. */
  landingVisible: boolean;
  falseStartThreshold?: number;
  /** Computed: ma jednotka super-admin povoleni skryt soutez z rozcestniku? */
  unitCanHideFromLanding: boolean;

  /** Hlavni rozhodci — pro PDF vysledkovku (podpis). Null = prazdny radek pro rucni podpis. */
  headJudgeName?: string | null;
  /** Velitel soutezi — pro PDF vysledkovku (podpis). */
  eventLeaderName?: string | null;
  /** Komisar soutezi — jen krajska/republikova kola. */
  commissarName?: string | null;

  templateId: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface Discipline {
  id: number;
  categoryId: number;
  name: string;
  type: DisciplineType;
  attemptsCount: number;
  sortOrder: number;
  plamenOrder: number;
  timeFormat: TimeFormat;
  useSecondTimer: boolean;
  isIndividual?: boolean;
  isDvojbojDisc?: boolean;
  teamMaxRunners?: number | null;
  teamScoringCount?: number | null;
  teamMinRequired?: number | null;
}

export interface Category {
  id: number;
  competitionId: number;
  name: string;
  ageGroup: AgeGroup;
  sortOrder: number;
  disciplines: Discipline[];
  teamCount: number;
}

export interface Team {
  id: number;
  categoryId: number;
  name: string;
  startNumber: number;
  sdhUnitName: string | null;
  note: string | null;
}

export interface CompetitionTemplate {
  id: number;
  unitId: number | null;
  name: string;
  description: string | null;
  isSystem: boolean;
  config: unknown;
  createdAt: string;
  updatedAt: string;
}

export interface SuperAdminDashboard {
  totalUnits: number;
  activeUnits: number;
  totalUsers: number;
  totalCompetitions: number;
  liveCompetitions: number;
  liveList: Competition[];
}

export const AGE_GROUP_LABELS: Record<AgeGroup, string> = {
  MLADSI_ZACI: 'Mladší žáci',
  STARSI_ZACI: 'Starší žáci',
  DOROSTENCI: 'Dorostenci',
  DOROSTENKY: 'Dorostenky',
  MUZI: 'Muži',
  ZENY: 'Ženy',
  OSTATNI: 'Ostatní',
};

export const DISCIPLINE_TYPE_LABELS: Record<DisciplineType, string> = {
  POZARNI_UTOK: 'Požární útok',
  STAFETA_DVOJIC: 'Štafeta požárních dvojic',
  STAFETA_4X60: 'Štafeta 4×60 m',
  STAFETA_4X100: 'Štafeta 4×100 m',
  BEH_60M_PREKAZKY: 'Běh 60 m s překážkami',
  BEH_100M_PREKAZKY: 'Běh 100 m s překážkami',
  BEH_100M_PHP: 'Běh 100 m s PHP',
  POZARNI_UTOK_CTIF: 'Požární útok s překážkami CTIF',
  STAFETA_CTIF: 'Štafeta CTIF (400 m)',
  VEZ: 'Výstup na cvičnou věž',
};

export const COMPETITION_STATUS_LABELS: Record<CompetitionStatus, string> = {
  DRAFT: 'Rozpracovaná',
  LIVE: 'Probíhá',
  FINISHED: 'Dokončená',
};

export const PUBLIC_LAYOUT_LABELS: Record<PublicResultsLayout, string> = {
  CLASSIC: 'Klasická tabulka',
  PODIUM: 'Sportovní podium',
  SPLIT_VIEW: 'Rozdělená obrazovka',
};

export const PUBLIC_LAYOUT_DESCRIPTIONS: Record<PublicResultsLayout, string> = {
  CLASSIC: 'Kompaktní tabulka jako teď — dobré pro tréninky a data-heavy přehled.',
  PODIUM: 'Top 3 karty (zlato/stříbro/bronz) + seznam pod tím — sportovní pocit pro diváky.',
  SPLIT_VIEW: 'Dvě obrazovky vedle sebe — aktuální disciplína + celkové pořadí.',
};

export const PUBLIC_THEME_LABELS: Record<PublicThemeMode, string> = {
  AUTO: 'Automaticky (podle layoutu)',
  LIGHT: 'Světlé',
  DARK: 'Tmavé',
};

export const BOARD_LAYOUT_LABELS: Record<BoardLayout, string> = {
  MATRIX: 'Matrix — všechny buňky',
  FOCUS: 'Focus — jedna dominantní',
  CAROUSEL: 'Carousel — rotace fullscreen',
  SPLIT: 'Split — matrix + focus',
  COLUMNS: 'Sloupce — kategorie vedle sebe',
};

export const BOARD_LAYOUT_DESCRIPTIONS: Record<BoardLayout, string> = {
  MATRIX: 'Grid všech kategorií × disciplín + QR vpravo. Pro přehled najednou.',
  FOCUS: 'Jedna disciplína dominantně (top 10) + ostatní jako menší karty. Auto-rotace.',
  CAROUSEL: 'Fullscreen jedna disciplína, rotuje po publicRotationSeconds. Ideální pro plátno.',
  SPLIT: 'Horní polovina matrix, dolní zvýrazněná aktuální disciplína.',
  COLUMNS: 'Výsledková tabule: sloupec pro každou kategorii s tabulkami disciplín a celkovým pořadím.',
};

export const BOARD_THEME_LABELS: Record<PublicThemeMode, string> = {
  AUTO: 'Automaticky (podle času / systému)',
  LIGHT: 'Světlé (čitelné ve dne)',
  DARK: 'Tmavé (čitelné v noci)',
};

export const BOARD_SORT_MODE_LABELS: Record<BoardSortMode, string> = {
  RANK: 'Podle pořadí (top)',
  START_NUMBER: 'Podle startovního čísla',
};

export interface LayoutVisibilityConfig {
  publicLayouts: Record<PublicResultsLayout, boolean>;
  boardLayouts: Record<BoardLayout, boolean>;
  publicDefault: PublicResultsLayout;
  boardDefault: BoardLayout;
}

export const ATHLETE_AGE_CATEGORY_LABELS: Record<AthleteAgeCategory, string> = {
  MLADSI_ZAK: 'Mladší žák',
  STARSI_ZAK: 'Starší žák',
  MLADSI_DOROSTENEC: 'Mladší dorostenec',
  STREDNI_DOROSTENEC: 'Střední dorostenec',
  STARSI_DOROSTENEC: 'Starší dorostenec',
  MLADSI_DOROSTENKA: 'Mladší dorostenka',
  STREDNI_DOROSTENKA: 'Střední dorostenka',
  STARSI_DOROSTENKA: 'Starší dorostenka',
  MUZ: 'Muž',
  ZENA: 'Žena',
  OSTATNI: 'Ostatní',
};

export interface Athlete {
  id: number;
  teamId: number;
  teamName?: string;
  firstName: string;
  lastName: string;
  birthYear?: number;
  gender: Gender;
  registrationNumber?: string;
  ageCategory: AthleteAgeCategory;
  note?: string;
  sortOrder?: number;
}

export interface CreateAthleteDto {
  firstName: string;
  lastName: string;
  birthYear?: number;
  gender: Gender;
  registrationNumber?: string;
  ageCategory: AthleteAgeCategory;
  note?: string;
  sortOrder?: number;
}

export interface IndividualRankingItem {
  rank: number | null;
  athleteId: number;
  firstName: string;
  lastName: string;
  birthYear?: number;
  ageCategory: AthleteAgeCategory;
  teamId: number | null;
  teamName: string | null;
  attemptTimesMs: (number | null)[];
  bestTimeMs: number | null;
  isNp: boolean;
  note?: string;
  /** Pocet falesnych startu per pokus (index 0 = pokus 1). */
  falseStartCountByAttempt?: (number | null)[];
}

export interface TeamSumScoringAthlete {
  athleteId: number;
  firstName: string;
  lastName: string;
  bestTimeMs: number;
}

export interface TeamSumRankingItem {
  rank: number | null;
  teamId: number;
  teamName: string;
  scoringCount: number;
  validAthletes: number;
  minRequired: number;
  totalTimeMs: number | null;
  contributors: TeamSumScoringAthlete[];
}

export interface SaveIndividualResultDto {
  athleteId: number;
  attemptNumber: number;
  timeMs?: number | null;
  isNp: boolean;
  note?: string;
  /** Pokud true, tento pokus byl falesny start. Backend inkrementuje counter a pri prekroceni threshold = NP. */
  falseStart?: boolean;
}
